import { Ref, onMounted, onUnmounted, watch, ref } from "vue";
import { container } from "tsyringe";

// shared
import TakeFirstPromiseProcessor from "@/shared/lib/async/TakeFirstPromiseProcessor";

interface UsePaginationOptions extends IntersectionObserverInit {}

interface UsePaginationProps {
    targetRef: Ref<HTMLElement | null>;
    callback: (...args: any) => Promise<any>;
    options?: UsePaginationOptions;
    onBeforeNextPage?: () => void;
    onAfterNextPage?: () => void;
}

/**
 * @description IntersectionObserver를 이용하여 페이지네이션을 구현합니다.
 * @param {UsePaginationProps} props : targetRef, callback, options
 * @param args : callback 함수에 전달할 인자
 */
const usePagination = (props: UsePaginationProps, ...args: any[]) => {
    // instance
    const takeFirstPromiseProcessor = container.resolve(TakeFirstPromiseProcessor);

    // intersection loading
    const intersectionLoading = ref(false);

    let io: IntersectionObserver | null = null;

    const initializeObserver = () => {
        if (!props.targetRef.value) return;

        // 이전 observer 를 제거합니다.
        destroyObserver();

        const ioOptions = {
            root: props.options?.root || null,
            rootMargin: props.options?.rootMargin || "0px",
            threshold: props.options?.threshold || 0.3,
        };

        // 요소와 마주쳤을 경우, callback 함수를 실행하 되, 첫번째 요청만 실행되도록 처리합니다.
        io = new IntersectionObserver((entries) => {
            entries.forEach(async (entry) => {
                if (entry.isIntersecting) {
                    // 이미 takeFirstPromiseProcessor 로 감싸주었지만, 내부 로직 자체는 intersection 이 될 때마다
                    // 실행됩니다. 이 조차 실행되지 않도록 처리하기 위해서 자체 loading 을 추가합니다.
                    if (intersectionLoading.value) return;

                    try {
                        intersectionLoading.value = true;

                        // 만일 페이지 로드 전 실행하고 싶은 로직이 있다면 여기서 실행됩니다.
                        props.onBeforeNextPage?.();

                        // promise 내부에서 state 를 변경하는것은 좋지 않다 생각이 듭니다.
                        // 다만 무한 스크롤이라는 intersection 을 이용해야 하는 특성상 이를 허용하고자 합니다.
                        // 이렇게 하기에 실제 DOM 까지 업데이트 되고 난 이후 intersectionLoading 이 false 가 됩니다.
                        await takeFirstPromiseProcessor.process(props.callback)(...args);

                        // 페이지 로드 이후 실행하고 싶은 로직이 있다면 여기서 실행됩니다.
                        props.onAfterNextPage?.();
                    } finally {
                        intersectionLoading.value = false;
                    }
                }
            });
        }, ioOptions);

        io.observe(props.targetRef.value);
    };

    const destroyObserver = () => {
        if (io) {
            if (props.targetRef.value) {
                io.unobserve(props.targetRef.value);
            }
            io.disconnect();
            io = null;
        }
    };

    // targetRef가 변경될 때마다 observer를 다시 초기화
    watch(
        () => props.targetRef.value,
        (newValue) => {
            if (newValue) {
                initializeObserver();
            }
        },
    );

    onMounted(initializeObserver);
    onUnmounted(destroyObserver);

    return {
        reinitialize: initializeObserver,
        destroy: destroyObserver,
    };
};

export default usePagination;
